import { skillModules } from '../data/skillModules'
import {
  AudienceLevel,
  ConversationRead,
  MatchedModule,
  SkillModule,
} from '../types/pathway'

const TERM_TRIGGERS: Record<string, string[]> = {
  leadership: ['leader', 'leadership', 'leading', 'manager', 'managers', 'supervis'],
  'change management': [
    'change',
    'reorg',
    'restructur',
    'transformation',
    'merger',
    'acquisition',
    'new operating model',
  ],
  communication: ['communicat', 'listen', 'messaging', 'presenting', 'clarity'],
  'team development': ['team', 'teams', 'collaborat', 'silo'],
  coaching: ['coach', 'one-on-one', '1:1', 'development plan', 'mentor'],
  delegation: ['delegat', 'bandwidth', 'overload', 'doing the work themselves'],
  feedback: ['feedback', 'performance review', 'annual review'],
  'emotional intelligence': [
    'emotional',
    'empathy',
    'burnout',
    'morale',
    'stress',
  ],
  conflict: ['conflict', 'tension', 'friction', 'pushback', 'resistance'],
  'strategic thinking': ['strategy', 'strategic', 'vision', 'roadmap', 'long term'],
  'stakeholder management': [
    'stakeholder',
    'cross-functional',
    'buy-in',
    'alignment',
  ],
  'executive presence': ['executive presence', 'board', 'gravitas', 'presence'],
  retention: ['turnover', 'attrition', 'retention', 'quitting', 'losing people'],
  'performance management': [
    'performance',
    'accountability',
    'underperform',
    'productivity',
  ],
  'decision making': ['decision', 'decisive', 'prioriti'],
  'business acumen': ['business outcome', 'revenue', 'margin', 'p&l', 'roi'],
  'project management': ['project', 'delivery', 'timeline', 'scope'],
}

const LEVEL_TRIGGERS: Record<AudienceLevel, string[]> = {
  'c-suite': ['c-suite', 'ceo', 'cfo', 'coo', 'chief', 'executive team', 'board'],
  'senior-leader': ['vp', 'vice president', 'director', 'senior leader', 'head of'],
  'mid-level': ['mid-level', 'middle manager', 'experienced manager', 'manager of managers'],
  'new-manager': [
    'new manager',
    'newly promoted',
    'first-time',
    'first time manager',
    'recently promoted',
    'promoted into',
  ],
  'individual-contributor': ['individual contributor', 'ic ', 'frontline', 'non-manager'],
}

export const ALL_TERMS: string[] = Object.keys(TERM_TRIGGERS)

const countOccurrences = (haystack: string, needle: string): number =>
  needle ? haystack.split(needle).length - 1 : 0

export function readConversation(text: string): ConversationRead {
  const lower = ` ${text.toLowerCase()} `

  const scored = ALL_TERMS.map((term) => ({
    term,
    hits: TERM_TRIGGERS[term].reduce(
      (total, trigger) => total + countOccurrences(lower, trigger),
      0,
    ),
  })).filter((entry) => entry.hits > 0)

  const strong = scored.filter((entry) => entry.hits > 1).map((e) => e.term)
  const weak = scored.filter((entry) => entry.hits === 1).map((e) => e.term)

  // If nothing was said twice, promote the first couple of single mentions so
  // the rep still has a usable search without having to pick anything.
  if (strong.length === 0) {
    strong.push(...weak.splice(0, 2))
  }

  const level =
    (Object.keys(LEVEL_TRIGGERS) as AudienceLevel[]).find((lvl) =>
      LEVEL_TRIGGERS[lvl].some((trigger) => lower.includes(trigger)),
    ) ?? null
  const sizeMatch = lower.match(
    /(\d+)\s*(people|employees|managers|leaders|staff|team members|directors)/,
  )
  return {
    terms: [...strong, ...weak],
    strong,
    weak,
    level,
    headcount: sizeMatch ? parseInt(sizeMatch[1], 10) : null,
    company: readCompany(text),
  }
}

/**
 * Pulls the account name out of the way reps actually write notes — "at
 * Meridian Health", "with Northwind", "Atlas Logistics is …". Capitalized
 * words only, so ordinary prose isn't mistaken for a company.
 */
function readCompany(text: string): string | null {
  const NAME = '([A-Z][A-Za-z&.\'-]+(?:\\s+[A-Z][A-Za-z&.\'-]+){0,3})'
  const patterns = [
    new RegExp(`\\b(?:at|with|for|from)\\s+${NAME}`),
    new RegExp(`\\b${NAME}\\s+(?:is|are|has|have|wants|needs|just)\\b`),
  ]

  // Common sentence openers and role words that look like names but aren't.
  const STOP = new Set([
    'i',
    'we',
    'they',
    'he',
    'she',
    'it',
    'the',
    'their',
    'our',
    'his',
    'her',
    'this',
    'that',
    'spoke',
    'talked',
    'met',
    'call',
    'vp',
    'ceo',
    'cto',
    'chro',
    'hr',
    'l&d',
    'head',
    'director',
    'manager',
    'people',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
  ])

  for (const pattern of patterns) {
    const match = text.match(pattern)
    if (!match) continue
    const words = match[1]
      .split(/\s+/)
      .filter((word) => !STOP.has(word.toLowerCase().replace(/[.,]$/, '')))
    const name = words.join(' ').replace(/[.,]$/, '').trim()
    if (name.length > 2) return name
  }

  return null
}

export function parseTopics(input: string): string[] {
  return input
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean)
}

function scoreModule(
  module: SkillModule,
  terms: string[],
  level: AudienceLevel | null,
): { score: number; matched: string[] } | null {
  const matched = module.keywords.filter((k) =>
    terms.some((t) => k.includes(t) || t.includes(k)),
  )
  const looseMatch = terms.filter((t) =>
    `${module.name} ${module.category} ${module.topics.join(' ')}`
      .toLowerCase()
      .includes(t),
  )
  const hits = Array.from(new Set([...matched, ...looseMatch]))
  if (hits.length === 0) return null

  let score = 44 + hits.length * 13
  if (level && module.levels.includes(level)) score += 16
  if (level && !module.levels.includes(level)) score -= 8
  if (terms[0] && module.keywords.some((k) => k.includes(terms[0]))) score += 8
  return { score: Math.max(32, Math.min(97, score)), matched: hits }
}

export function matchModules(
  terms: string[],
  level: AudienceLevel | null,
): MatchedModule[] {
  if (terms.length === 0) {
    return skillModules.map((module) => ({
      module,
      score: null,
      matchedTerms: [],
    }))
  }
  return skillModules
    .map((module) => {
      const result = scoreModule(module, terms, level)
      if (!result) return null
      return {
        module,
        score: result.score,
        matchedTerms: result.matched,
      }
    })
    .filter((m): m is MatchedModule => m !== null)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
}

export function relatedTerms(terms: string[], results: MatchedModule[]): string[] {
  const counts = new Map<string, number>()
  results.slice(0, 12).forEach(({ module }) => {
    module.keywords.forEach((k) => {
      if (terms.includes(k)) return
      counts.set(k, (counts.get(k) ?? 0) + 1)
    })
  })
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([term]) => term)
}

export function matchBand(score: number): 'Strong' | 'Good' | 'Partial' {
  if (score >= 75) return 'Strong'
  if (score >= 58) return 'Good'
  return 'Partial'
}


